/** Academy-only cache boundary. No bearer token is ever written to a query key. */
import { useAuthStore } from '@neuroforge/shared/client'
import type { QueryClient } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'

import { revokeAllLessonBlobs } from './lesson-blob-registry'

let revision = 0
const listeners = new Set<() => void>()

function notify() {
  revision += 1
  listeners.forEach((listener) => listener())
}

/**
 * A Zustand subscription is synchronous: destroy cached A data and cancel active
 * queries before React can render a new principal/tier. Older in-flight results
 * may still settle in a detached query but can never populate B's keyed cache.
 */
export function isolateAcademyQueries(client: QueryClient): () => void {
  return useAuthStore.subscribe((next, previous) => {
    if (
      next.accessToken !== previous.accessToken ||
      next.isAuthenticated !== previous.isAuthenticated ||
      next.user?.id !== previous.user?.id ||
      next.user?.role !== previous.user?.role ||
      next.user?.status !== previous.user?.status ||
      next.user?.subscriptionTier !== previous.user?.subscriptionTier
    ) {
      revokeAllLessonBlobs()
      client.clear()
      notify()
    }
  })
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot() {
  return revision
}

/** Revision is also used as an image key to revoke object URLs on auth changes. */
export function useAcademySessionScope(): string {
  const version = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const userId = useAuthStore((state) => state.user?.id ?? 'anonymous')
  const tier = useAuthStore((state) => state.user?.subscriptionTier ?? 'FREE')
  const role = useAuthStore((state) => state.user?.role ?? 'ANONYMOUS')
  const status = useAuthStore((state) => state.user?.status ?? 'ANONYMOUS')
  const authenticated = useAuthStore((state) => state.isAuthenticated)
  return `${version}:${authenticated ? userId : 'anonymous'}:${role}:${status}:${tier}`
}

/** Safe to consult in async mutation callbacks without holding a bearer token. */
export function getAcademySessionRevision(): number {
  return revision
}
